export const ROOM_GAME_PHASES = ['waiting', 'playing', 'ended'] as const
export type RoomGamePhase = (typeof ROOM_GAME_PHASES)[number]

export interface RundotGameRoomRulesGameState extends Record<string, unknown> {
  phase?: RoomGamePhase
  currentPlayer?: string | null
  turnOrder?: string[]
  turnCount?: number
  turnBased?: boolean
  playerStates?: Record<string, unknown>
  startParams?: {
    gameConfig: Record<string, unknown>
    turnOrder?: string[] | null
    startedBy: string
    startedAt: unknown
  }
}

export interface RundotGameRoomRules extends Record<string, unknown> {
  hostProfileId?: string
  minPlayers?: number
  maxPlayers?: number
  turnBased?: boolean
  gameState?: RundotGameRoomRulesGameState
}

export interface RundotGameRoomCustomMetadata extends Record<string, unknown> {
  rules?: RundotGameRoomRules
}

export interface RundotGameRoomPayload extends Record<string, unknown> {
  id: string
  name?: string
  players?: string[]
  maxPlayers?: number
  gameType?: string
  appId?: string
  type?: string
  createdBy?: string
  createdAt?: number
  updatedAt?: number
  isPrivate?: boolean
  status?: string
  customMetadata?: RundotGameRoomCustomMetadata
  admins?: string[]
  roomCode?: string
  description?: string
  data?: Record<string, unknown>
  version?: number
}

/**
 * Server API response envelopes.
 * These match the format returned by the H5 Rooms Cloud Functions.
 */

/**
 * Standard envelope for endpoints that return a single room.
 */
export interface RoomEnvelopeResponse {
  success: boolean
  room: RundotGameRoomPayload
}

/**
 * Standard envelope for endpoints that return multiple rooms.
 */
export interface RoomsEnvelopeResponse {
  success: boolean
  rooms: RundotGameRoomPayload[]
}

/**
 * Standard envelope for join-or-create room endpoint.
 */
export interface JoinOrCreateRoomEnvelopeResponse {
  success: boolean
  action: 'created' | 'joined'
  room: RundotGameRoomPayload
  playersJoined: number
}

export class RundotGameRoom {
  public readonly id: string
  public name?: string
  public players: string[]
  public maxPlayers?: number
  public gameType?: string
  public appId?: string
  public type?: string
  public createdBy?: string
  public createdAt?: number
  public updatedAt?: number
  public isPrivate?: boolean
  public status?: string
  public customMetadata: RundotGameRoomCustomMetadata
  public admins: string[]
  public roomCode?: string
  public description?: string
  public data: Record<string, unknown>
  public version?: number

  constructor(roomData: RundotGameRoomPayload) {
    this.id = roomData.id
    this.name = roomData.name
    this.players = Array.isArray(roomData.players)
      ? [...roomData.players]
      : []
    this.maxPlayers = roomData.maxPlayers
    this.gameType = roomData.gameType
    this.appId = roomData.appId
    this.type = roomData.type
    this.createdBy = roomData.createdBy
    this.createdAt = roomData.createdAt
    this.updatedAt = roomData.updatedAt
    this.isPrivate = roomData.isPrivate
    this.status = roomData.status
    this.customMetadata = roomData.customMetadata || {}
    this.admins = Array.isArray(roomData.admins) ? [...roomData.admins] : []
    this.roomCode = roomData.roomCode
    this.description = roomData.description
    this.data = roomData.data || {}
    this.version = roomData.version
  }
}
